import React, { useState, useEffect, useCallback } from 'react';
import { search, getStats, logout, removeToken } from '../api';
import { useAuth } from '../App';

export default function Dashboard({ onSelectRef }) {
  const { user, setUser } = useAuth();
  const [filters, setFilters] = useState({ reference: '', acteur: '' });
  const [activeFilters, setActiveFilters] = useState({ reference: '', acteur: '' });
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    getStats().then(d => setStats(d.data)).catch(() => {});
    doSearch({ reference: '', acteur: '' }, 1);
  }, []);

  const doSearch = useCallback(async (f, page = 1) => {
    setLoading(true);
    try {
      const res = await search({ ...f, page, limit: 30 });
      setResults(res.data);
      setPagination(res.pagination);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const handleSearch = () => { setActiveFilters(filters); doSearch(filters, 1); };
  const handleReset = () => {
    setFilters({ reference: '', acteur: '' });
    setActiveFilters({ reference: '', acteur: '' });
    doSearch({ reference: '', acteur: '' }, 1);
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };
  const handleLogout = async () => {
    try { await logout(); } catch {}
    removeToken(); setUser(null);
  };
  const changePage = (p) => { setPagination(prev => ({ ...prev, page: p })); doSearch(activeFilters, p); };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  // Nettoyage des noms d'acteurs
  const cleanActeurs = (str) => {
    if (!str) return '—';
    return str.split(', ')
      .map(a => a
        .replace(/^ACTOR\s+/i, '')
        .replace(/^SCENE\s+/i, '')
        .replace(/^PHOTO\s+/i, '')
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
      )
      .filter((v, i, a) => a.indexOf(v) === i) // dédoublonner
      .join(', ');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <header style={{
        background: 'var(--bg-2)', borderBottom: '1px solid var(--border)',
        padding: '0 40px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 24,
          fontWeight: 700, color: 'var(--text)', letterSpacing: 0.5
        }}>
          Admin<span style={{ color: 'var(--accent)' }}>Films</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          {stats && (
            <div style={{ display: 'flex', gap: 24 }}>
              {[
                { label: 'Références', val: stats.total_refs },
                { label: 'Fichiers', val: stats.total_fichiers },
                { label: 'Acteurs', val: stats.total_acteurs }
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 500, color: 'var(--accent)', lineHeight: 1 }}>
                    {s.val?.toLocaleString()}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginTop: 2 }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ width: 1, height: 32, background: 'var(--border)' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--accent)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500
            }}>
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{user?.username}</div>
              <button onClick={handleLogout} style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer',
                fontFamily: 'var(--font-body)'
              }}
                onMouseEnter={e => e.target.style.color = 'var(--accent)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Barre de recherche */}
      <div style={{
        background: 'var(--bg-2)', borderBottom: '1px solid var(--border)',
        padding: '20px 40px'
      }}>
        <div style={{ display: 'flex', gap: 12, maxWidth: 900 }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block', fontFamily: 'var(--font-mono)',
              fontSize: 10, letterSpacing: 2, color: 'var(--text-muted)',
              textTransform: 'uppercase', marginBottom: 6
            }}>Référence</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text" placeholder="ex: MF003157"
                value={filters.reference}
                onChange={e => setFilters(f => ({ ...f, reference: e.target.value }))}
                onKeyDown={handleKeyDown}
                style={{
                  width: '100%', padding: '11px 16px',
                  border: '1.5px solid var(--border)', borderRadius: 4,
                  fontSize: 15, color: 'var(--text)', background: 'var(--bg)',
                  outline: 'none', transition: 'border-color 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
              {filters.reference && (
                <button onClick={() => setFilters(f => ({ ...f, reference: '' }))}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: 'var(--text-muted)',
                    cursor: 'pointer', fontSize: 18, lineHeight: 1
                  }}>×</button>
              )}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block', fontFamily: 'var(--font-mono)',
              fontSize: 10, letterSpacing: 2, color: 'var(--text-muted)',
              textTransform: 'uppercase', marginBottom: 6
            }}>Acteur</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text" placeholder="Nom de l'acteur"
                value={filters.acteur}
                onChange={e => setFilters(f => ({ ...f, acteur: e.target.value }))}
                onKeyDown={handleKeyDown}
                style={{
                  width: '100%', padding: '11px 16px',
                  border: '1.5px solid var(--border)', borderRadius: 4,
                  fontSize: 15, color: 'var(--text)', background: 'var(--bg)',
                  outline: 'none', transition: 'border-color 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
              {filters.acteur && (
                <button onClick={() => setFilters(f => ({ ...f, acteur: '' }))}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: 'var(--text-muted)',
                    cursor: 'pointer', fontSize: 18, lineHeight: 1
                  }}>×</button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <button onClick={handleSearch} style={{
              padding: '11px 28px', background: 'var(--accent)', border: 'none',
              color: '#fff', fontSize: 13, fontWeight: 600,
              letterSpacing: 1, cursor: 'pointer', borderRadius: 4,
              transition: 'background 0.2s', fontFamily: 'var(--font-body)'
            }}
              onMouseEnter={e => e.target.style.background = '#6d1414'}
              onMouseLeave={e => e.target.style.background = 'var(--accent)'}
            >Rechercher</button>
            <button onClick={handleReset} style={{
              padding: '11px 20px', background: 'none',
              border: '1.5px solid var(--border)', color: 'var(--text-dim)',
              fontSize: 13, cursor: 'pointer', borderRadius: 4,
              transition: 'all 0.2s', fontFamily: 'var(--font-body)'
            }}
              onMouseEnter={e => { e.target.style.borderColor = 'var(--border-dark)'; e.target.style.color = 'var(--text)'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-dim)'; }}
            >Réinitialiser</button>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div style={{ flex: 1, padding: '28px 40px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 20
        }}>
          <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 16 }}>
              {pagination.total?.toLocaleString()}
            </span>{' '}
            référence{pagination.total !== 1 ? 's' : ''}
            {(activeFilters.reference || activeFilters.acteur) ? ' trouvée' + (pagination.total !== 1 ? 's' : '') : ''}
          </div>
          {loading && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: 2 }}>
              CHARGEMENT...
            </div>
          )}
        </div>

        {/* Tableau */}
        <div style={{
          background: 'var(--bg-2)', border: '1px solid var(--border)',
          borderRadius: 8, overflow: 'hidden', boxShadow: 'var(--shadow-sm)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-3)', borderBottom: '2px solid var(--border)' }}>
                {['Référence', 'Acteurs', 'Fichiers', 'Indexé le'].map(h => (
                  <th key={h} style={{
                    padding: '14px 20px', textAlign: 'left',
                    fontFamily: 'var(--font-mono)', fontSize: 10,
                    letterSpacing: 2, color: 'var(--text-muted)',
                    textTransform: 'uppercase', fontWeight: 500
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} style={{
                    padding: '60px', textAlign: 'center',
                    color: 'var(--text-muted)', fontFamily: 'var(--font-mono)',
                    fontSize: 12, letterSpacing: 3
                  }}>AUCUN RÉSULTAT</td>
                </tr>
              ) : results.map(row => (
                <tr
                  key={row.id}
                  onClick={() => onSelectRef(row.reference)}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: hoveredRow === row.id ? 'var(--accent-light)' : 'transparent',
                    transition: 'background 0.15s'
                  }}
                >
                  <td style={{ padding: '16px 20px' }}>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 13,
                      fontWeight: 500, color: 'var(--accent)'
                    }}>{row.reference}</span>
                  </td>
                  <td style={{
                    padding: '16px 20px', fontSize: 14,
                    color: 'var(--text-dim)', maxWidth: 400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {cleanActeurs(row.acteurs)}
                  </td>
                  <td style={{
                    padding: '16px 20px', textAlign: 'center',
                    fontFamily: 'var(--font-mono)', fontSize: 13,
                    color: 'var(--text-dim)'
                  }}>
                    <span style={{
                      background: 'var(--bg-3)', padding: '3px 10px',
                      borderRadius: 12, fontSize: 12
                    }}>{row.nb_fichiers}</span>
                  </td>
                  <td style={{
                    padding: '16px 20px',
                    fontFamily: 'var(--font-mono)', fontSize: 12,
                    color: 'var(--text-muted)'
                  }}>{formatDate(row.date_indexation)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'center',
            alignItems: 'center', gap: 6, padding: '24px 0'
          }}>
            <button
              onClick={() => changePage(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                padding: '8px 16px', border: '1.5px solid var(--border)',
                background: 'var(--bg-2)', color: 'var(--text-dim)',
                borderRadius: 4, cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                opacity: pagination.page <= 1 ? 0.4 : 1, fontSize: 13
              }}
            >← Précédent</button>

            {[...Array(Math.min(pagination.totalPages, 7))].map((_, i) => {
              const p = i + 1;
              const isActive = p === pagination.page;
              return (
                <button key={p} onClick={() => changePage(p)} style={{
                  padding: '8px 14px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
                  border: isActive ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                  background: isActive ? 'var(--accent)' : 'var(--bg-2)',
                  color: isActive ? '#fff' : 'var(--text-dim)',
                  fontWeight: isActive ? 600 : 400
                }}>{p}</button>
              );
            })}

            <button
              onClick={() => changePage(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              style={{
                padding: '8px 16px', border: '1.5px solid var(--border)',
                background: 'var(--bg-2)', color: 'var(--text-dim)',
                borderRadius: 4, cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer',
                opacity: pagination.page >= pagination.totalPages ? 0.4 : 1, fontSize: 13
              }}
            >Suivant →</button>
          </div>
        )}
      </div>
    </div>
  );
}
